import React from 'react';
import useBreakpoint from '@sportson/core-web/hooks/useBreakpoint';
import styled from '@grebban/style-system-react';

import { type BackgroundColorProps } from '@sportson/core-web/components/BackgroundColor';
import { type BackgroundImageProps } from '@sportson/core-web/components/BackgroundImage';
import { type BackgroundVideoProps } from '@sportson/core-web/components/BackgroundVideo';
import DiscoverComponent, { type DiscoverComponentProps } from 'content/Discover';

import {
    type WordpressBackgroundSelection,
    type WordpressFlexibleNavigation,
    type WordpressHeadingGroup,
    type WordpressParagraphGroup,
} from 'libs/wordpress/types/contentSystem/groups';
import { transformBackgroundGroup } from 'libs/wordpress/utils/transformBackground';
import { transformLinkButton } from 'libs/wordpress/utils/transformButton';
import { transformHeadingGroup } from 'libs/wordpress/utils/transformHeading';
import { transformParagraphGroup } from 'libs/wordpress/utils/transformParagraph';
import { ArrowIcon } from 'assets/media/icons/ArrowIcon';

const Div = styled('div')``;

export interface WordpressDiscoverBlock {
    mediaBackground: WordpressBackgroundSelection;
    heading: WordpressHeadingGroup;
    paragraph: WordpressParagraphGroup;
    navigation: WordpressFlexibleNavigation;
}

// TODO: Remove Record<string, unknown> when styling is fixed, correctly pass css via rest
export interface DiscoverBlockProps extends Record<string, unknown> {
    data: WordpressDiscoverBlock;
    layout?: 'discover' | 'twoColumns' | 'threeColumns';
}

const DiscoverBlock: React.FunctionComponent<DiscoverBlockProps> = ({ data, layout, ...rest }) => {
    const { heading, mediaBackground, navigation, paragraph } = data;
    const componentData: DiscoverComponentProps = {
        aspectRatio: ';',
    };
    let isUsedInColumn = false;

    const isMobile = useBreakpoint({ to: 'tablet.sm' });
    const device = isMobile ? 'mobile' : 'desktop';

    // Background
    if (mediaBackground) {
        const { type: backgroundType, data: backgroundData } = transformBackgroundGroup(mediaBackground, device);
        if (backgroundType === 'color') {
            componentData.backgroundColor = backgroundData as BackgroundColorProps;
        } else if (backgroundType === 'image') {
            componentData.backgroundImage = backgroundData as BackgroundImageProps;
        } else if (backgroundType === 'video') {
            componentData.backgroundVideo = backgroundData as BackgroundVideoProps;
        }
    }

    // Heading
    if (heading) {
        componentData.heading = transformHeadingGroup(heading, device);
    }

    // Link
    if (navigation) {
        const linkType = navigation[0].layout;
        if (linkType === 'buttons') {
            componentData.buttons = navigation[0].buttons.map((button) => {
                const tButton = transformLinkButton(button.button);
                return {
                    url: tButton.link,
                    children: (
                        <>
                            {tButton.label}
                            <ArrowIcon />
                        </>
                    ),
                    theme: tButton.theme,
                };
            });
        } else if (linkType === 'content') {
            componentData.contentLink = navigation[0].link;
        }
    }

    // Text
    if (paragraph) {
        componentData.paragraph = transformParagraphGroup(paragraph, device);
    }

    if (layout) {
        isUsedInColumn = layout === 'twoColumns' || layout === 'threeColumns';

        componentData.aspectRatio = isUsedInColumn
            ? ['3/2', null, null, null, '631/420']
            : ['3/4', null, '368/245', null, '643/241'];

        componentData.headingTypography = isUsedInColumn
            ? 'Content/24_130_0_450'
            : ['Content/24_130_0_450', null, null, null, 'Content/40_120_0_500'];
    }

    return (
        <Div {...rest}>
            <DiscoverComponent
                flexDirection={isUsedInColumn ? 'column' : ['column', null, null, null, 'row']}
                gap={isUsedInColumn ? '16px' : ['16px', null, null, null, '80px']}
                {...componentData}
            />
        </Div>
    );
};

export default DiscoverBlock;
